import { useEffect, useState } from 'react';
import { navigate } from 'gatsby';
import Cookies from 'universal-cookie';

interface LanguageVersion {
  language: string;
  href: string;
}

interface UsePreferredLanguageProps {
  language: string;
  languageVersions: LanguageVersion[];
}

const usePreferredLanguage = ({
  language,
  languageVersions,
}: UsePreferredLanguageProps): string => {
  const [preferredLanguageHref, setPreferredLanguageHref] = useState(`/${language}/`);

  // Initialize cookies instance
  const cookies = new Cookies();

  useEffect(() => {
    // Retrieve preferred language from cookies or browser settings
    const userLangCookie = cookies.get('preferredLanguage');
    const userLang = userLangCookie || navigator.language || (navigator as any).userLanguage;
    const langCode = userLang.split('-')[0]; // Extract primary language code

    // Create a set of available languages from language versions
    const availableLanguages = new Set(languageVersions.map((lv) => lv.language));

    // Determine preferred language based on availability or default to 'en'
    const preferredLanguage = availableLanguages.has(langCode) ? langCode : 'en';

    // Update preferred language if different from current language
    if (language !== preferredLanguage) {
      const preferredVersion = languageVersions.find((lv) => lv.language === language);

      if (preferredVersion) {
        const href = preferredVersion.href.replace('/homepage', '').replace(/\/?$/, '/');
        setPreferredLanguageHref(href);

        // Update cookies with preferred language
        cookies.set('preferredLanguage', language, { path: '/' });

        // Navigate to new language version
        navigate(href, { replace: true });
      } else {
        // Handle case where preferred language version not found
        cookies.set('preferredLanguage', 'en', { path: '/' });
      }
    } else {
      // Set cookies if not already set for the current language
      if (!cookies.get('preferredLanguage')) {
        cookies.set('preferredLanguage', preferredLanguage, { path: '/' });
      }
      setPreferredLanguageHref(`/${language}/`.replace(/\/+/g, '/'));
    }
  }, [language, languageVersions, cookies]);

  return preferredLanguageHref;
};

export default usePreferredLanguage;
